/**
 * @summary - Component that Provides Refresh and Export(csv,xlxs) functionality
 * @author Purushothaman T
 * @since 2024-05-30
 */
import LoopRoundedIcon from "@mui/icons-material/LoopRounded";
import SaveAltRoundedIcon from "@mui/icons-material/SaveAltRounded";
import { IconButton, Stack } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Tooltip from "@mui/material/Tooltip";
import { reportsLogo } from "../../assets/images/base64";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';

const RefreshExportButtons = ({
  title,
  refData,
  handleRefresh,
  customPdf,
  exportData,
  exportFunction,
  formattedData,
  formattedHeaders,
  setCustomData,
  ppm = false,
  isContract = false,
  pagination = true,
  customObj = null,
  enablePDF = true,
  enableDownload = true
}) => {

  const gridApi = refData;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const excludedHeaders = ["Actions", "Profile Picture"];



  const getFilteredData = () => {
    if (gridApi) {
      // Get all displayed columns, excluding those in the excludedHeaders array
      const columns = gridApi
        .getAllDisplayedColumns()
        .filter((col) => !excludedHeaders.includes(col.getColDef()?.headerName))
        .map((col) => ({
          field: col.colId,
          header: col.getColDef()?.headerName,
          type: col.getColDef()?.cellDataType
        }));

      return columns;
    } else {
      return [];
    }
  };

  const getDisplayedData = () => {
    if (gridApi) {
      // Get the list of displayed columns (excluding excluded headers)
      const displayedColumns = gridApi
        .getAllDisplayedColumns()
        .filter((col) => !excludedHeaders.includes(col.getColDef()?.headerName))
        .map((col) => ({
          colId: col.colId,
          headerName: col.getColDef()?.headerName, // Store the headerName for mapping
        }));

      // Retrieve all currently displayed rows
      const rowData = [];
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        const row = {};
        displayedColumns.forEach(({ colId, headerName }) => {
          row[headerName] = node.data[colId]; // Map the data to the headerName
        });
        rowData.push(row);
      });

      return rowData;
    } else {
      return [];
    }
  };

  const getDisplayedCustomData = () => {
    if (gridApi) {
      // Get the list of displayed columns (excluding excluded headers)
      const displayedColumns = gridApi
        .getAllDisplayedColumns()
        .filter((col) => !excludedHeaders.includes(col.getColDef()?.headerName))
        .map((col) => ({
          colId: col.colId,
          field: col.getColDef()?.field, // Store the field name for mapping
        }));

      // Retrieve all currently displayed rows
      const rowData = [];
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        const row = {};
        displayedColumns.forEach(({ colId, field }) => {
          row[field] = node.data[colId]; // Map the data to the field name
        });
        rowData.push(row);
      });

      return rowData;
    } else {
      return [];
    }
  };

  const getAllGridData = () => {
    if (gridApi) {
      // Retrieve rows that are visible after filtering and sorting
      const rowData = [];
      gridApi.forEachNodeAfterFilterAndSort((node) => {
        rowData.push({ ...node.data }); // Directly copy all fields from node.data
      });

      return rowData;
    } else {
      return [];
    }
  };



  function transformCustomData(customObj, dataArray) {
    return dataArray.map((dataItem) => {
      const transformedItem = {};

      Object.entries(customObj).forEach(([key, value]) => {
        transformedItem[value] = dataItem[key] || ""; // Assign data or empty string if not found
      });

      return transformedItem;
    });
  }

  function filterAndRename(propertiesArray, objectsArray, isPdfExport = false) {
    return objectsArray.map(obj => {
      return propertiesArray.reduce((filteredObj, prop) => {
        // Map to correct property or set to empty string
        const value = obj.hasOwnProperty(prop.field)
          ? (isPdfExport || prop.type !== "text" ? obj[prop.field] : `\u200B${obj[prop.field]}`)
          : ""; // Empty string if property is not present

        filteredObj[prop.header] = value; // Use header for the renamed property
        return filteredObj;
      }, {});
    });
  }

  const onExportCSV = async () => {
    let data;
    if (pagination) {
      const filteredData = getFilteredData();
      data = filterAndRename(filteredData, exportData);
    } else if (customObj) {
      const allData = getAllGridData();
      data = transformCustomData(customObj, allData)
    } else {
      data = getDisplayedData();
    }

    // If data is empty, get column headers from gridApi
    if (!data || data.length === 0) {
      const columns = gridApi
        .getAllDisplayedColumns()
        .filter(
          (col) =>
            col.getColDef().headerName !== "Actions" &&
            col.getColDef().headerName !== "Profile Picture"
        )
        .map((col) => col.getColDef().headerName);

      // Convert columns to CSV format (just the header row)
      const worksheet = XLSX.utils.aoa_to_sheet([columns]);

      // Create a workbook and append the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, title);

      // Write to a CSV file
      XLSX.writeFile(workbook, `${title}.csv`, { bookType: "csv" });
    } else {
      // Convert JSON data to CSV format
      const worksheet = XLSX.utils.json_to_sheet(data);

      // Create a workbook and append the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, title);

      // Write to a CSV file
      XLSX.writeFile(workbook, `${title}.csv`, { bookType: "csv" });
    }
  };



  const onExportExcel = async () => {
    let data;
    if (pagination) {
      const filteredData = getFilteredData();
      data = filterAndRename(filteredData, exportData);
    } else if (customObj) {
      const allData = getAllGridData();
      data = transformCustomData(customObj, allData);
    } else {
      data = getDisplayedData();
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(title);

    // Determine column headers
    const columns =
      data && data.length > 0
        ? Object.keys(data[0])
        : gridApi
          .getAllDisplayedColumns()
          .filter(
            (col) =>
              col.getColDef().headerName !== "Actions" &&
              col.getColDef().headerName !== "Profile Picture"
          )
          .map((col) => col.getColDef().headerName);

    // Ensure a minimum number of columns
    const maxColumns = columns.length || 1;

    // Add header row
    const headerRow = worksheet.addRow(columns);

    // Style header row
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    });

    if (!data || data.length === 0) {
      // Add "No records found" row
      const noDataRow = worksheet.addRow(["No records found"]);

      // Merge cells across all columns
      worksheet.mergeCells(noDataRow.number, 1, noDataRow.number, maxColumns);

      // Style "No records found" row
      noDataRow.getCell(1).font = { color: { argb: "#000" } };
      noDataRow.getCell(1).alignment = { vertical: "middle", horizontal: "center" };
      noDataRow.getCell(1).border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      };
    } else {
      // Add data rows and apply border styles even if empty
      // Add data rows and apply border styles even if empty
      // Add data rows and apply border styles even if empty
      data.forEach((item) => {
        const rowValues = columns.map(col => {
          const value = item[col];
          return value !== null && value !== undefined ? value : '\u200B';
        });

        const dataRow = worksheet.addRow(rowValues);

        // Apply border styles and alignment based on data type
        dataRow.eachCell((cell, colNumber) => {
          const fieldName = columns[colNumber - 1]; // Get field name from columns
          const value = item[fieldName];

          // Ensure numeric fields are treated as numbers
          const isNumeric = typeof value === 'number' || (!isNaN(value) && value !== null && value !== '');

          // Force cell type to be number if it contains a numeric value
          if (isNumeric) {
            cell.value = Number(value); // Ensure Excel recognizes it as a number
          }

          cell.alignment = {
            vertical: "middle",
            horizontal: isNumeric ? "left" : "right",
          };

          cell.border = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });


    }

    // Set column widths
    worksheet.columns.forEach((column) => {
      column.width = 20; // Set width for all columns
    });

    // Write to a file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };




  const onExportPdf = () => {
    let allRowData;
    if (pagination) {
      const filteredData = getFilteredData();
      allRowData = filterAndRename(filteredData, exportData, true);
    } else {
      allRowData = getDisplayedData();
    }
    const doc = new jsPDF(isContract ? "l" : "p", "pt", "a4");

    const totalPagesExp = "{total_pages_count_string}";
    const imgData = `data:image/png;base64,${reportsLogo}`; // Replace with actual base64 string of your PNG image

    const formatDate = (date) => {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Intl.DateTimeFormat("en-US", options).format(date);
    };

    const dateStr = formatDate(new Date());
    const columns = gridApi
      .getAllDisplayedColumns()
      .filter(
        (col) =>
          col.getColDef().headerName !== "Actions" &&
          col.getColDef().headerName !== "Profile Picture"
      )
      .map((col) => col.getColDef().headerName);

    const tableRows = allRowData.map((rowData) =>
      columns.map((header) => rowData[header])
    );


    const headerStyle = {
      fillColor: [185, 149, 213],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    };

    const bodyStyle = {
      textColor: [0, 0, 0],
      width: 0.5,
    };

    if (!allRowData || allRowData.length === 0) {
      doc.autoTable({
        head: [columns],
        body: [
          [
            {
              content: "No records found",
              colSpan: columns.length,
              styles: {
                halign: "center",
                fontStyle: "thin",
                textColor: [100, 100, 100],
              },
            },
          ],
        ],
        theme: "striped",
        styles: {
          fontSize: 10,
          cellPadding: 4,
        },
        headStyles: {
          ...headerStyle,
        },
        margin: { top: 50, left: 14, right: 14 },
        didDrawPage: (data) => {
          const pageWidth = doc.internal.pageSize.getWidth();
          doc.addImage(imgData, "PNG", pageWidth - 80, 5, 60, 40);

          doc.setFontSize(12);
          const titleText = `${title}`;
          const titleWidth = doc.getTextWidth(titleText);
          doc.text(titleText, (pageWidth - titleWidth) / 2, 20);

          doc.setFontSize(8);
          doc.text(`${dateStr}`, 14, 35);

          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height
            ? pageSize.height
            : pageSize.getHeight();
          doc.setFontSize(10);
          doc.text(`${title}`, 14, pageHeight - 10);

          let str = `Page ${data.pageNumber}`;
          if (typeof doc.putTotalPages === "function") {
            str = `${data.pageNumber}/${totalPagesExp}`;
          }
          doc.text(str, pageSize.width - 24, pageHeight - 10);
        },
      });
    } else {
      doc.autoTable({
        head: [columns],
        body: tableRows,
        theme: "striped",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "visible",
          lineColor: [162, 169, 177],
          lineWidth: 0.5,
        },
        headStyles: {
          ...headerStyle,
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
        },
        bodyStyles: {
          ...bodyStyle,
          overflow: "linebreak",
        },
        columnStyles: columns.reduce((acc, header, index) => {
          if (header === "Scheduled Frequency") {
            acc[index] = { halign: "center" }; // Center align for Scheduled Frequency
          } else {
            acc[index] = { cellWidth: "auto" }; // Default alignment for other columns
          }
          return acc;
        }, {}),
        tableWidth: "auto",
        margin: { top: 50, left: 14, right: 14 },
        didDrawPage: (data) => {
          const pageWidth = doc.internal.pageSize.getWidth();
          doc.addImage(imgData, "PNG", 14, 5, 60, 40);

          doc.setFontSize(12);
          const titleText = `${title}`;
          const titleWidth = doc.getTextWidth(titleText);
          doc.text(titleText, (pageWidth - titleWidth) / 2, 20);

          doc.setFontSize(8);
          doc.text(`${dateStr}`, pageWidth - 80, 35);

          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height
            ? pageSize.height
            : pageSize.getHeight();
          doc.setFontSize(10);
          doc.text(`${title}`, 14, pageHeight - 10);

          let str = `Page ${data.pageNumber}`;
          if (typeof doc.putTotalPages === "function") {
            str = `${data.pageNumber}/${totalPagesExp}`;
          }
          doc.text(str, pageSize.width - 24, pageHeight - 10);
        },
      });

    }

    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }

    doc.save(`${title}.pdf`);
  };


  const handlePdfExport = () => {
    if (customPdf) {
      customPdf();
    } else {
      onExportPdf();
    }
  };



  const handleToggle = () => {
    setOpen((prevOpen) => {
      if (!prevOpen) {
        const data = getDisplayedCustomData()
        exportFunction()
        if (setCustomData) {
          setCustomData(data)
        }
      }
      return !prevOpen
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  const prevOpen = React.useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleGridRefresh = () => {
    gridApi.setFilterModel(null);
    gridApi.applyColumnState({
      defaultState: { sort: null },
    })

    gridApi.paginationProxy.setPageSize(10, "pageSizeSelector");
    const columns = gridApi.getColumns();
    const hiddenColumns = columns.filter((column) => !column.visible);
    hiddenColumns.forEach((column) => {
      gridApi.setColumnVisible(column, true);
    });
    gridApi.refreshHeader();
    handleRefresh();
  };

  const exportToExcel = async (headersData, data, title) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(title);

    // Create header and subheader rows
    const headerRow = worksheet.addRow(headersData.map(col => col.header || ''));
    const subHeaderRow = worksheet.addRow(headersData.map(col => col.subHeader || ''));

    // Merge header cells
    let lastHeader = null;
    let mergeStartIndex = -1;

    headersData.forEach((col, index) => {
      const header = col.header;
      if (header !== lastHeader) {
        if (mergeStartIndex !== -1 && index > mergeStartIndex) {
          worksheet.mergeCells(1, mergeStartIndex + 1, 1, index);
        }
        mergeStartIndex = index;
        lastHeader = header;
      }
    });

    // Final merge for last header group
    if (mergeStartIndex !== -1 && mergeStartIndex < headersData.length - 1) {
      worksheet.mergeCells(1, mergeStartIndex + 1, 1, headersData.length);
    }

    // Apply styles to header and subheader rows
    [headerRow, subHeaderRow].forEach((row) => {
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // Add data rows
    // Add data rows
    // Add data rows
    data.forEach(item => {
      const rowValues = headersData.map(col => {
        const value = item[col.field];

        // Ensure empty values are handled correctly
        return value !== undefined && value !== null ? value : '\u200B';
      });

      const dataRow = worksheet.addRow(rowValues);

      // Apply styles to each cell
      dataRow.eachCell((cell, colNumber) => {
        const field = headersData[colNumber - 1]?.field; // Get field name from headersData
        const value = item[field];

        // Determine if the value is numeric
        const isNumeric = typeof value === 'number' || (!isNaN(value) && value !== null && value !== '');

        if (isNumeric) {
          cell.value = Number(value); // Convert to actual number
        }

        cell.alignment = {
          vertical: 'middle',
          horizontal: isNumeric ? 'right' : 'left', // Align numbers to right
        };

        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

    });



    // Set column widths
    worksheet.columns.forEach(column => {
      column.width = 20;
    });

    // Write to a file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };


  const exportToCsv = (headersData, data) => {
    const items = headersData
    if (ppm) {
      items[0].header = "Work Group:"
    }


    const csvHeader = items.map(col => col.header || '').join(',') + '\n';
    const csvSubHeader = headersData.map(col => col.subHeader || '').join(',') + '\n';

    const csvRows = data.map(item =>
      headersData.map(col => {
        // Get the value and handle commas and quotes
        let value = item[col.field] || '';
        if (typeof value === 'string') {
          // Escape double quotes by doubling them
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    ).join('\n');

    const csvContent = csvHeader + csvSubHeader + csvRows;

    // Create a Blob and download the CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };




  return (
    <>
      <Stack direction={"row"} alignItems={"flex-end"}>
        <Tooltip
          title="Refresh"
          placement="bottom"
          arrow
          disableFocusListener
          disableInteractive
        >
          <IconButton onClick={handleGridRefresh}>
            <LoopRoundedIcon />
          </IconButton>
        </Tooltip>

        {enableDownload && <Tooltip
          title="Export"
          placement="bottom"
          arrow
          disableFocusListener
          disableInteractive
        >
          <IconButton
            variant="contained"
            ref={anchorRef}
            onClick={handleToggle}
          >
            <SaveAltRoundedIcon />
          </IconButton>
        </Tooltip>}
      </Stack>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        sx={{ zIndex: 5, width: 100 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >
                  <MenuItem onClick={formattedData ? () => exportToCsv(formattedHeaders, formattedData) : onExportCSV}>csv</MenuItem>
                  <MenuItem onClick={formattedData ? () => exportToExcel(formattedHeaders, formattedData) : onExportExcel}>xlsx</MenuItem>
                  {enablePDF && <MenuItem onClick={handlePdfExport}>
                    pdf
                  </MenuItem>}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default RefreshExportButtons;
